import {
  type GradeCategory,
  type SubjectWithLessons,
} from "@core/grades/types";
import { type BreadcrumbItem } from "@core/shared/types";
import { URLS } from "@core/shared/utils";
import { trans } from "@mongez/localization";
import { type ChangeEvent, useCallback, useMemo, useState } from "react";

export function useSubjectDetailsPage(subject: SubjectWithLessons) {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategoryId, setActiveCategoryId] = useState<number | null>(null);

  const pageData = useMemo(() => {
    const lessons = subject.lessons || [];
    const categoriesById = new Map<number, GradeCategory>();

    lessons.forEach((lesson) => {
      if (lesson.category) {
        categoriesById.set(lesson.category.id, lesson.category);
      }
    });

    const categories = Array.from(categoriesById.values());
    const breadcrumbs: BreadcrumbItem[] = [
      {
        text: trans("grades.title"),
        url: URLS.grades.list,
      },
      {
        text: subject.name,
        url: URLS.subjects.view(subject),
      },
    ];

    return {
      breadcrumbs,
      lessons,
      categories,
      totalLessons: subject.total?.lessons || lessons.length,
      totalCategories: subject.total?.categories || categories.length,
    };
  }, [subject]);

  const filteredLessons = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLocaleLowerCase();

    return pageData.lessons.filter((lesson) => {
      const matchesCategory =
        activeCategoryId === null || lesson.category?.id === activeCategoryId;
      const searchableText = [
        lesson.name,
        lesson.category?.name,
        lesson.instructor?.name,
      ]
        .filter(Boolean)
        .join(" ")
        .toLocaleLowerCase();

      return (
        matchesCategory &&
        (!normalizedQuery || searchableText.includes(normalizedQuery))
      );
    });
  }, [activeCategoryId, pageData.lessons, searchQuery]);

  const handleSearchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(event.target.value);
    },
    [],
  );

  const handleCategoryChange = useCallback((categoryId: number | null) => {
    setActiveCategoryId(categoryId);
  }, []);

  return {
    ...pageData,
    activeCategoryId,
    filteredLessons,
    handleCategoryChange,
    handleSearchChange,
    searchQuery,
  };
}
